import { Button, Flex, Form, Input, message, Modal, Select, Tag, Tooltip, Typography } from "antd";
import { ApiService } from "api/api.service";
import RichTextEditor from "components/RichTextEditor";
import { EmailAccountConfig, SystemEmailStaticTags } from "dtos/email.dto";
import { useEffect, useMemo, useState } from "react";
import { TbSend, TbPaperclip, TbLoader2, TbSparkles } from "react-icons/tb";
import { useParams, useSearchParams } from "react-router-dom";
import { formatMoney, getErrorMessage, getRiskPlusEmail, toTitleCase } from "utils/functions";
import { MdArrowBack, MdOutlineKeyboardDoubleArrowRight } from "react-icons/md";
import ChartDownloadModal from "./AprChartModal";
import { SlDocs } from "react-icons/sl";
import GenerateOfferModal from "./GenerateOfferModal";
import { T_MongoId } from "types/_utils.types";
import AttachmentsModal, { allowedExtensions, allowedMimeTypes } from "./attachments";

interface ComposeEmailProps {
  attachedProfile: EmailAccountConfig[];
  selectedProfileId?: string;
  toggleNegotiationPanel: () => void;
  isPanelOpen: boolean;
  isInboxPanelOpen: boolean;
  openInboxPanel: () => void;
  updateNegotiationContext: (context: {
    application_id?: string;
    loan_id?: string;
    thread_id?: string;
    message_id?: string;
  }) => void;
  closeInboxPanel: () => void;
}

export type ContentType =
  | "image"
  | "video"
  | "audio"
  | "pdf"
  | "text"
  | "archive"
  | "spreadsheet"
  | "document"
  | "presentation"
  | "binary"
  | "unknown";

export const getContentTypeFromFile = (file: File): ContentType => {
  const mime = file.type;
  const ext = file.name.split(".").pop()?.toLowerCase();

  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";

  if (mime === "application/pdf" || ext === "pdf") return "pdf";

  if (mime.startsWith("text/") || ["txt", "md", "csv"].includes(ext ?? "")) return "text";

  if (["zip", "rar", "7z", "tar", "gz"].includes(ext ?? "")) return "archive";

  if (
    mime === "application/vnd.ms-excel" ||
    mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    ["xls", "xlsx"].includes(ext ?? "")
  )
    return "spreadsheet";

  if (
    mime === "application/msword" ||
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    ["doc", "docx"].includes(ext ?? "")
  )
    return "document";

  if (
    mime === "application/vnd.ms-powerpoint" ||
    mime === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    ["ppt", "pptx"].includes(ext ?? "")
  )
    return "presentation";

  if (!mime && ext) return "binary";

  return "unknown";
};

type ComposeFormData = {
  subject: string;
  loan: T_MongoId | undefined;
  from: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  communication_type: SystemEmailStaticTags | undefined;
};

const ComposeEmail: React.FC<ComposeEmailProps> = ({
  attachedProfile,
  selectedProfileId,
  closeInboxPanel,
  isInboxPanelOpen,
  isPanelOpen,
  openInboxPanel,
  toggleNegotiationPanel,
  updateNegotiationContext,
}) => {
  const [searchParams] = useSearchParams();
  const { app_key, orgCode } = useParams<{ app_key: string; orgCode: string }>();
  const emailSubject = decodeURIComponent(searchParams.get("subject") || "");
  const chatId = decodeURIComponent(searchParams.get("ncId") || "");
  const parentChatId = decodeURIComponent(searchParams.get("pncId") || "");
  const loanId = decodeURIComponent(searchParams.get("loanId") || "");
  const sendAsOrgRaw = decodeURIComponent(searchParams.get("sendAsOrg") || "");
  const isSendAsOrgInitial = sendAsOrgRaw === "true";
  const fromApp = searchParams.get("fromApp");
  const selectedLoanFromParams = searchParams.get("selectedLoan");
  // const { templates } = useEmailTemplates();
  // const emailBodyRaw = decodeURIComponent(searchParams.get("body") || "");
  const [isOpen, setIsOpen] = useState(false);
  const formatEmailToHtml = (text: string) => {
    return text
      .replace(/\n+/g, "\n")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => `<p>${line}</p>`)
      .join("");
  };

  const emailBodyRaw = decodeURIComponent(searchParams.get("body") || "");
  const emailBody = formatEmailToHtml(emailBodyRaw);

  const organizationProfiles = ApiService.email.useGetOrganisationsProfiles({
    orgCode: orgCode,
  });
  const allOrgProfiles = useMemo(() => {
    return organizationProfiles.data?.result || [];
  }, [organizationProfiles.data]);
  const [form] = Form.useForm<ComposeFormData>();

  const [isSendAsOrg, setIsSendAsOrg] = useState(isSendAsOrgInitial);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const sendEmail = ApiService.email.useSendEmail();
  const [appKeyState, setAppKeyState] = useState(app_key || fromApp || "");
  const [isofferGeneratorPanelOpen, setIsofferGeneratorPanelOpen] = useState(false);
  const fullApplication = ApiService.application.useGetByAppKey(appKeyState);
  const loanDetails = useMemo(
    () => fullApplication.data?.data.application.loans ?? [],
    [fullApplication.data?.data.application.loans],
  );
  const [showVariablesModal, setShowVariablesModal] = useState(false);
  const [chartsModalOpen, setChartsModalOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  // const [selectedLoan, setSelectedLoan] = useState<string[]>([]);
  const [selectedLoanContext, setSelectedLoanContext] = useState<string | undefined>(undefined);
  const [filter, setFilter] = useState<{ page: number; limit: number; search: string }>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [applicationFilter, setApplicationFilter] = useState({
    page: 1,
    limit: 50,
    search: "",
  });
  const allApplicationsQuery = ApiService.application.useGetPaginatedForOrg({ ...applicationFilter });
  const creditorsByAppKey = ApiService.creditor.useGetPaginatedByAppKey({ appKey: appKeyState, page: 1, limit: 100 });
  const firstOutbound = ApiService.negotiation.useFirstOutbound();
  // const allCreditors = creditorsByAppKey.data?.result.data ?? [];
  const allApplications = allApplicationsQuery.data?.result.data ?? [];
  const emailSuggestions = ApiService.email.useGetEmailSuggestions({ appKey: app_key ?? appKeyState, ...filter });
  const getaprChartsRes = ApiService.email.useGetAprCharts(getRiskPlusEmail(fullApplication.data?.application.app_key));
  const suggestedEmail = emailSuggestions.data?.result.data ?? [];
  const [body, setBody] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [templateVariables, setTemplateVariables] = useState<string[]>([]);
  const [variablesWithValues, setVariablesWithValues] = useState<Record<string, string>>({});
  const selectedApplication = Form.useWatch("application", form);
  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);

  const selectedLoan = loanDetails.find((l) => l._id === selectedLoanFromParams);

  useEffect(() => {
    const selectedLoanId = selectedLoan?._id;
    if (!selectedLoanId) return;
    form.setFieldValue("loan", selectedLoanId);
    setSelectedLoanContext(selectedLoanId);
  }, [selectedLoan?._id]);

  const sendEmailFrom = useMemo(() => {
    if (selectedProfileId) {
      return orgCode || isSendAsOrg
        ? allOrgProfiles.find((profile) => profile.isDefaultSender)
        : attachedProfile.find((profile) => profile._id === selectedProfileId);
    }
    return orgCode || isSendAsOrg ? allOrgProfiles[0] : attachedProfile[0];
  }, [attachedProfile, selectedProfileId, allOrgProfiles, orgCode, isSendAsOrg]);

  // Set initial form values when sendEmailFrom changes
  useMemo(() => {
    if (sendEmailFrom) {
      form.setFieldsValue({ from: sendEmailFrom._id });
    }
  }, [sendEmailFrom, form, loanId]);
  useMemo(() => {
    form.setFieldsValue({ subject: emailSubject, loan: loanId || undefined });
    setBody(emailBody);
  }, [emailSubject, emailBody, loanId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const mismatchedFileTypes = fileArray.filter((file) => {
        // Primary check: MIME type
        if (file.type && allowedMimeTypes.has(file.type)) {
          return false;
        }

        // Fallback: extension check (important for docs/xlsx)
        const ext = file.name.split(".").pop()?.toLowerCase();
        return !ext || !allowedExtensions.has(ext);
      });

      if (mismatchedFileTypes.length > 0) {
        message.error("Only images, PDFs, Word, and Excel files are allowed");
        return;
      }
      const oversizedFiles = fileArray.filter((file) => file.size > 25 * 1024 * 1024); // 25MB limit

      if (oversizedFiles.length > 0) {
        message.error(`Some files exceed 25MB limit: ${oversizedFiles.map((f) => f.name).join(", ")}`);
        return;
      }

      setAttachedFiles((prev) => [...prev, ...fileArray]);
      // message.success(`${fileArray.length} file(s) attached`);
    }

    event.target.value = "";
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    // message.success("File removed");
  };

  const replaceVariables = (content: string, variables: Record<string, string>): string => {
    return content.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
      const trimmedVar = variable.trim();
      return variables[trimmedVar] ?? match;
    });
  };

  const getUnfilledVariables = (): string[] => {
    if (templateVariables.length === 0) return [];

    return templateVariables.filter((variable) => {
      const value = variablesWithValues[variable];
      return !value || value.trim() === "";
    });
  };

  const handleSendFirstOutBound = () => {
    // firstOutbound.mutate(
    //   {
    //     body: { appkey: appKeyState, loan_id: selectedLoanContext },
    //   },
    //   {
    //     onSuccess: () => {
    //       updateNegotiationContext({
    //         application_id: fullApplication.data?.application._id,
    //         loan_id: selectedLoanContext,
    //       });
    //       toggleNegotiationPanel();
    //       closeInboxPanel();
    //     },
    //   },
    // );
    updateNegotiationContext({
      application_id: fullApplication.data?.application._id,
      loan_id: selectedLoanContext,
    });
    toggleNegotiationPanel();
    closeInboxPanel();
  };

  const handleInsertImageIntoBody = (dataUrl: string, index: number) => {
    const file = attachedFiles[index];
    if (!file) return;
    const imgTag = `<img src="${dataUrl}" alt="${file.name}" style="max-width:100%;" />`;
    setBody((prev) => prev + imgTag);
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendEmail = (values: ComposeFormData) => {
    // Check if template has variables that need to be filled
    if (templateVariables.length > 0) {
      const unfilledVariables = getUnfilledVariables();

      if (unfilledVariables.length > 0) {
        message.error(`Please fill all template variables. Missing: ${unfilledVariables.join(", ")}`);
        setShowVariablesModal(true);
        return;
      }
    }

    // Replace variables in subject and body
    const finalSubject =
      templateVariables.length > 0 ? replaceVariables(values.subject || "", variablesWithValues) : values.subject || "";

    const finalBody = templateVariables.length > 0 ? replaceVariables(body, variablesWithValues) : body;

    sendEmail.mutate(
      {
        appKey: app_key || appKeyState,
        to: (values.to ?? []).map((email: string) => email.trim()),
        cc: values.cc || [],
        bcc: values.bcc || [],
        subject: finalSubject,
        body: finalBody,
        from: values.from,
        loan: values.loan,
        communication_type: values.communication_type,
        attachments: attachedFiles,
        negotiation_id: chatId,
      },
      {
        onSuccess: () => {
          form.resetFields();
          setBody("");
          setAttachedFiles([]);
          setTemplateVariables([]);
          setVariablesWithValues({});
          message.success("Email sent successfully");
        },
      },
    );
  };

  const updateVariableValues = (values: Record<string, string>) => {
    setVariablesWithValues(values);
  };

  // const templateOptions: { value: string; label: ReactNode }[] = templates.map((tmpl) => ({
  //   value: tmpl.id,
  //   label: <Flex>{tmpl.config.name}</Flex>,
  // }));

  // templateOptions.unshift({ label: <Flex>Blank Template</Flex>, value: "blank_template" });

  return (
    <div className="bg-white p-6  rounded-lg w-full max-w-7xl mx-auto ">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1">
          {!isInboxPanelOpen && (
            <Tooltip title="Open inbox">
              <Button
                variant="text"
                type="text"
                onClick={openInboxPanel}
                icon={<MdOutlineKeyboardDoubleArrowRight size={20} />}
              ></Button>
            </Tooltip>
          )}
          <Typography.Title level={3} style={{ margin: 0 }}>
            Compose New Email
          </Typography.Title>
        </div>
        <div className="flex items-center gap-2">
          {appKeyState &&
            selectedLoanContext &&
            (isPanelOpen ? null : (
              <Button
                variant="solid"
                color="magenta"
                icon={<TbSparkles />}
                onClick={handleSendFirstOutBound}
                style={{ background: "transparent", overflow: "hidden", isolation: "isolate" }}
                className="!shadow-lg"
              >
                <div className="mesh_bg" />
                Suggestions
              </Button>
            ))}
        </div>
      </div>
      <Form requiredMark={"optional"} layout="vertical" form={form} className="space-y-4" onFinish={handleSendEmail}>
        <Flex className="!w-full" gap={6}>
          <Form.Item
            className="w-full"
            hidden={!orgCode}
            label="Select Application"
            name="application"
            initialValue={fromApp}
            rules={[{ required: !!orgCode, message: "Please select an application" }]}
          >
            <Select
              showSearch
              variant="underlined"
              filterOption={false}
              loading={allApplicationsQuery.isPending}
              style={{ width: "100%" }}
              placeholder="Search by application name or appkey"
              onChange={(val) => {
                // Reset search filter when an email is selected
                setApplicationFilter((prev) => ({ ...prev, search: "" }));
                setAppKeyState(val);
                form.setFieldsValue({ loan: undefined });
                form.setFieldsValue({ to: [] });
              }}
              onSearch={(value) => {
                setApplicationFilter((prev) => ({ ...prev, search: value }));
              }}
              options={allApplications.map((app) => ({
                label: (
                  <Typography.Text>
                    {app?.meta?.business_name || app.meta.client_name || app?.lead?.name || app?.app_key}{" "}
                    <span className="font-mono opacity-70">({app.app_key})</span>
                  </Typography.Text>
                ),
                value: app.app_key,
              }))}
              notFoundContent={
                allApplicationsQuery.isPending ? (
                  <div className="flex flex-col items-center gap-2 justify-center">
                    <TbLoader2 size={24} className="animate-spin stroke-purple-600" />
                    <Typography.Text>Finding applications...</Typography.Text>
                  </div>
                ) : applicationFilter.search ? (
                  <Typography.Paragraph style={{ textAlign: "center", padding: "8px 0" }}>
                    No applications found
                  </Typography.Paragraph>
                ) : (
                  <Typography.Paragraph style={{ textAlign: "center", padding: "8px 0" }}>
                    Start typing to search
                  </Typography.Paragraph>
                )
              }
            />
          </Form.Item>
          <Form.Item
            className="w-full"
            // hidden={Boolean(selectedLoanFromParams)}
            label="Select Loan"
            name="loan"
          >
            <Select
              allowClear
              variant="underlined"
              style={{ width: "100%" }}
              placeholder="Select a loan"
              loading={fullApplication.isPending}
              onChange={(value) => {
                // const selected = allCreditors.find((loan) => loan._id === value);
                // setSelectedLoan(selected?.allowed_emails ?? []);
                setSelectedLoanContext(value);
                updateNegotiationContext({
                  application_id: fullApplication.data?.application._id,
                  loan_id: selectedLoanContext,
                });
              }}
              options={loanDetails.map((loan) => ({
                label: loan.creditor_name,
                value: loan._id,
                fundedAmount: loan.funded_amount,
                payments: loan.payments,
                frequency: loan.frequency,
              }))}
              labelRender={(option) => {
                const loan = loanDetails.find((loan) => loan._id === option.value);
                if (!loan?.funded_amount) return option.label;
                return `${loan.creditor_name} (${formatMoney(loan.funded_amount)})`;
              }}
              optionRender={(option) => (
                <div>
                  <Typography.Text>{option.data.label}</Typography.Text>
                  {option.data.fundedAmount ? (
                    <div className="text-xs opacity-80">
                      {formatMoney(option.data.fundedAmount)} funded
                      {option.data.payments ? (
                        <>
                          {"; "}
                          {formatMoney(option.data.payments)} {option.data.frequency}
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            />
          </Form.Item>
        </Flex>
        <Flex className="!w-full" gap={6}>
          <Form.Item
            className="w-1/2"
            label="From"
            name="from"
            rules={[
              {
                required: true,
                message: "Please enter valid email recipient",
              },
            ]}
          >
            <Select
              variant="underlined"
              style={{ width: "100%" }}
              placeholder="Enter email addresses"
              options={
                orgCode || isSendAsOrg
                  ? (organizationProfiles.data?.result ?? []).map((email) => ({ label: email.email, value: email._id }))
                  : attachedProfile.map((email) => ({ label: email.email, value: email._id }))
              }
            />
          </Form.Item>
          <Form.Item
            className="w-1/2"
            label="To"
            name="to"
            rules={[
              {
                required: true,
                message: "Please enter at least one recipient",
                validator: (_, value) => {
                  //each value should be a valid email
                  if (value && value.length > 0) {
                    const invalidEmails = value.filter((email: string) => !/\S+@\S+\.\S+/.test(email));
                    if (invalidEmails.length > 0) {
                      return Promise.reject("Please enter valid email addresses");
                    }
                    return Promise.resolve();
                  }
                  return Promise.reject("Please enter at least one recipient");
                },
              },
            ]}
          >
            <Select
              variant="underlined"
              mode="tags"
              loading={emailSuggestions.isPending}
              style={{ width: "100%" }}
              placeholder="Enter email addresses"
              onChange={() => {
                // Reset search filter when an email is selected
                setFilter((prev) => ({ ...prev, search: "" }));
              }}
              onSearch={(value) => {
                setFilter((prev) => ({ ...prev, search: value }));
              }}
              options={
                // selectedLoan.length > 0
                //   ? selectedLoan?.map((email) => ({ label: email, value: email })) :
                suggestedEmail.map((email) => ({ label: email, value: email }))
              }
              notFoundContent={
                emailSuggestions.isLoading ? (
                  <div className="flex flex-col items-center gap-2 justify-center">
                    <TbLoader2 size={24} className="animate-spin stroke-purple-600" />
                    <Typography.Text>Finding items...</Typography.Text>
                  </div>
                ) : filter.search ? (
                  <Typography.Paragraph style={{ textAlign: "center", padding: "8px 0" }}>
                    No applications found
                  </Typography.Paragraph>
                ) : (
                  <Typography.Paragraph style={{ textAlign: "center", padding: "8px 0" }}>
                    {selectedApplication ? "Start typing to search" : "Select an application first"}
                  </Typography.Paragraph>
                )
              }
            />
          </Form.Item>
        </Flex>
        <Flex className="!w-full" gap={6}>
          <Form.Item
            className="w-1/2"
            // hidden={!showCcBcc}
            label="Cc"
            name="cc"
            rules={[
              {
                message: "Please enter valid email addresses",
                validator: (_, value) => {
                  //each value should be a valid email
                  if (value && value.length > 0) {
                    const invalidEmails = value.filter((email: string) => !/\S+@\S+\.\S+/.test(email));
                    if (invalidEmails.length > 0) {
                      return Promise.reject("Please enter valid email addresses");
                    }
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              variant="underlined"
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Enter email addresses"
              onChange={() => {
                // Reset search filter when an email is selected
                setFilter((prev) => ({ ...prev, search: "" }));
              }}
              onSearch={(value) => {
                setFilter((prev) => ({ ...prev, search: value }));
              }}
              options={suggestedEmail.map((email) => ({ label: email, value: email }))}
            />
          </Form.Item>
          <Form.Item
            className="w-1/2"
            // hidden={!showCcBcc}
            label="Bcc"
            name="bcc"
            rules={[
              {
                message: "Please enter valid email addresses",
                validator: (_, value) => {
                  //each value should be a valid email
                  if (value && value.length > 0) {
                    const invalidEmails = value.filter((email: string) => !/\S+@\S+\.\S+/.test(email));
                    if (invalidEmails.length > 0) {
                      return Promise.reject("Please enter valid email addresses");
                    }
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              variant="underlined"
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Enter email addresses"
              onChange={() => {
                // Reset search filter when an email is selected
                setFilter((prev) => ({ ...prev, search: "" }));
              }}
              onSearch={(value) => {
                setFilter((prev) => ({ ...prev, search: value }));
              }}
              options={suggestedEmail.map((email) => ({ label: email, value: email }))}
            />
          </Form.Item>
        </Flex>
        {/* <div className="w-full flex justify-end">
          <Button type="text" onClick={() => setShowCcBcc(!showCcBcc)}>
            {showCcBcc ? "Hide Cc/Bcc" : "Add Cc/Bcc"}
          </Button>
        </div> */}

        <Form.Item
          hidden={!orgCode}
          label="Communication Type"
          name="communication_type"
          rules={[{ message: "Select communication type" }]}
        >
          <Select
            variant="underlined"
            style={{ width: "100%" }}
            placeholder="Select communication Type"
            options={Object.values(SystemEmailStaticTags).map((tag) => ({
              label: toTitleCase(tag, { sep: "_" }),
              value: tag,
            }))}
          />
        </Form.Item>
        <Form.Item label="Subject" name="subject" rules={[{ required: true, message: "Please enter the subject" }]}>
          <Input variant="underlined" placeholder="Email Subject" />
        </Form.Item>

        <Form.Item label="Body" className="min-h-48">
          <RichTextEditor value={body} onChange={setBody} />
        </Form.Item>

        <Flex className="w-full items-end justify-between">
          <Form.Item className="!my-0">
            <input type="file" id="file-input" multiple onChange={handleFileSelect} className="!hidden" />

            <Button icon={<TbPaperclip size={16} />} onClick={() => setIsAttachModalOpen(true)}>
              Attachments {attachedFiles.length ? <Tag>{attachedFiles.length}</Tag> : null}
            </Button>

            <AttachmentsModal
              open={isAttachModalOpen}
              onClose={() => setIsAttachModalOpen(false)}
              attachedFiles={attachedFiles}
              onRemoveFile={handleRemoveFile}
              onUploadClick={() => document.getElementById("file-input")?.click()}
              isFileLimitReached={attachedFiles.length >= 5}
              onOpenChartsPicker={() => setIsOpen(true)}
              chartsDisabled={
                !appKeyState || !getaprChartsRes.data?.result?.charts?.length || attachedFiles.length >= 5
              }
              chartsError={getaprChartsRes.isError ? getErrorMessage(getaprChartsRes.error) : undefined}
              onInsertImageIntoBody={handleInsertImageIntoBody}
            />
          </Form.Item>
          <div className="flex gap-4 justify-end">
            {templateVariables.length > 0 && (
              <Button
                onClick={() => setShowVariablesModal(true)}
                icon={<TbSend size={20} />}
                variant="outlined"
                color="purple"
              >
                Fill Variables
              </Button>
            )}
            <Button
              disabled={body.trim() === ""}
              icon={<TbSend size={20} />}
              type="primary"
              htmlType="submit"
              loading={sendEmail.isPending}
            >
              Send
            </Button>
          </div>
        </Flex>
      </Form>
      <FillVariablesModal
        open={showVariablesModal}
        variables={templateVariables}
        onUpdate={updateVariableValues}
        onClose={() => setShowVariablesModal(false)}
      />
      <ChartDownloadModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        charts={getaprChartsRes.data?.result.charts ?? []}
        applicationId={getRiskPlusEmail(fullApplication.data?.application.app_key) as string}
        onAttach={(files, totalAttempted) => {
          setAttachedFiles((prev) => [...prev, ...files]);
          if (files.length === totalAttempted) setIsOpen(false);
        }}
      />
      <GenerateOfferModal
        application_id={fullApplication.data?.application._id as string}
        loanId={selectedLoanContext as string}
        onClose={() => setIsofferGeneratorPanelOpen(false)}
        open={isofferGeneratorPanelOpen}
        thread_id={undefined}
        message_id={undefined}
      />
    </div>
  );
};

export default ComposeEmail;

const FillVariablesModal: React.FC<{
  variables: string[];
  onUpdate: (values: Record<string, string>) => void;
  open: boolean;
  onClose: () => void;
}> = ({ variables, onUpdate, open, onClose }) => {
  const [form] = Form.useForm();

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      onUpdate(values);
      onClose();
    });
  };

  return (
    <Modal
      title="Fill Template Variables"
      open={open}
      onCancel={() => {
        onClose();
        form.resetFields();
      }}
      onOk={handleSubmit}
    >
      {/* it will be two column layout with variable name on left and input field on right */}
      <Form form={form} layout="vertical">
        {variables.map((variable) => (
          <div key={variable} className="flex">
            <Typography.Text className="w-[200px]">{variable} : </Typography.Text>
            <Form.Item
              className="flex-1"
              layout="horizontal"
              name={variable}
              rules={[{ required: true, message: `Please enter a value for ${variable}` }]}
            >
              <Input placeholder={`Enter value for ${variable}`} />
            </Form.Item>
          </div>
        ))}
      </Form>
    </Modal>
  );
};
